import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Paper, IconButton, Tooltip, Chip, Tabs, Tab, Badge, Avatar, CardActionArea, Drawer, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import { FaFilePdf, FaClock } from 'react-icons/fa';
import { MdDescription, MdLabelOutline, MdEdit, MdTransform } from 'react-icons/md';
import { useFavorites } from '../../context/FavoritesContext';
import { usePermissions } from '../../context/PermissionContext';
import { TOOL_COLORS } from '../../constants/colors';

const RECENT_VISITS_KEY = 'recentPageVisits';

const PDFLanding = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { favorites, toggleFavorite, isFavorite } = useFavorites();
  const { isAdmin, isOwner, hasAccessToPath } = usePermissions();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedTool, setSelectedTool] = useState(null);
  const [lastUsedMap, setLastUsedMap] = useState({});

  // Load last used times from localStorage
  useEffect(() => {
    const loadLastUsedTimes = () => {
      try {
        const stored = localStorage.getItem(RECENT_VISITS_KEY);
        if (stored) {
          const visits = JSON.parse(stored);
          const timeMap = {};
          visits.forEach(visit => {
            if (!timeMap[visit.path]) {
              timeMap[visit.path] = new Date(visit.timestamp);
            }
          });
          setLastUsedMap(timeMap);
        }
      } catch (err) {
        console.error('Error loading last used times:', err);
      }
    };
    loadLastUsedTimes();
  }, []);

  // Format time ago from date
  const formatTimeAgo = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / 60000);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks} week${diffInWeeks !== 1 ? 's' : ''} ago`;
  };

  // Get last used time for a path
  const getLastUsed = (path) => {
    return formatTimeAgo(lastUsedMap[path]);
  };


  const getToolIcon = (name) => {
    if (name.toLowerCase().includes('label')) return MdLabelOutline;
    if (name.toLowerCase().includes('invoice')) return MdDescription;
    if (name.toLowerCase().includes('manipulation') || name.toLowerCase().includes('modifier')) return MdEdit;
    if (name.toLowerCase().includes('excel')) return MdTransform;
    return FaFilePdf;
  };

  const pdfInterfaces = [
    {
      name: 'AMA9999 UCC Labels',
      path: '/dashboard/ama9999-ucc-labels',
      description: 'Generate and print UCC labels for AMA9999 products with barcode integration.',
      category: 'Labels',
      isNew: false,
    },
    {
      name: 'Amazon DI UCC Label',
      path: '/dashboard/amazon-di-ucc-label',
      description: 'Create compliant UCC labels specifically formatted for Amazon Direct Import Orders.',
      category: 'Labels',
      isNew: false,
    },
    {
      name: 'Back Labels',
      path: '/dashboard/bilingual-labels',
      description: 'Generate multilingual back labels with care instructions and country information for collections.',
      category: 'Labels',
      isNew: false,
    },
    {
      name: 'EU-UCC PDF Modifier - Item',
      path: '/dashboard/carton-labels',
      description: 'Modify and customize UCC labels for European Union compliance requirements.',
      category: 'Editing',
      isNew: false,
    },
    {
      name: 'Expense Allocation Invoices',
      path: '/dashboard/expense-allocation-invoices',
      description: 'Generate PDF invoices for expense allocations across departments.',
      category: 'Invoices',
      isNew: false,
    },
    {
      name: 'Home Goods PDF',
      path: '/dashboard/home-goods-pdf',
      description: 'Create PDF catalogs and documentation for home goods products.',
      category: 'Documents',
      isNew: false,
    },
    {
      name: 'PDF Manipulation',
      path: '/dashboard/pdf-manipulation',
      description: 'Comprehensive PDF editing tools including merge, split, rotate, and watermark.',
      category: 'Editing',
      isNew: false,
    },
    {
      name: 'PDF to Excel (UCC Labels)',
      path: '/dashboard/pdf-to-excel-ucc-labels',
      description: 'Convert PDF UCC labels to Excel format for data analysis.',
      category: 'Conversion',
      isNew: false,
    },
    {
      name: "WAL4000 PDF's",
      path: '/dashboard/pallet-labels',
      description: 'Generate and manage PDF documents specifically for WAL4000 product lines.',
      category: 'Documents',
      isNew: false,
    },
    {
      name: 'PDQ Full Pallet Pull Label',
      path: '/dashboard/pdq-full-pallet-pull-label',
      description: 'Generate shipping labels and case marking labels with GTIN barcodes.',
      category: 'Labels',
      isNew: false,
    },
    {
      name: 'CAD Invoices',
      path: '/dashboard/cad-invoices',
      description: 'Generate and manage invoices in Canadian Dollar (CAD) currency.',
      category: 'Invoices',
      isNew: false,
    },
    {
      name: 'Master Retail Rugs',
      path: '/dashboard/master-retail-rugs',
      description: 'Generate rug gallery labels by Design, Item ID, or Web ID. Labels sorted by rug size.',
      category: 'Labels',
      isNew: false,
    },
    {
      name: 'Label Printing',
      path: '/dashboard/label-printing',
      description: 'Generate UPC/QR code labels and custom rug labels with multiple size options.',
      category: 'Labels',
      isNew: false,
    }
  ];

  const categories = ['All', 'Labels', 'Editing', 'Invoices', 'Documents', 'Conversion'];

  // Filter interfaces based on user permissions
  const permittedInterfaces = useMemo(() => {
    if (isAdmin || isOwner) {
      return pdfInterfaces;
    }
    return pdfInterfaces.filter(item => {
      // Extract path without /dashboard/ prefix for permission check
      const path = item.path.replace('/dashboard/', '');
      return hasAccessToPath(path);
    });
  }, [isAdmin, isOwner, hasAccessToPath]);

  const filteredTools = selectedTab === 0
    ? permittedInterfaces
    : permittedInterfaces.filter(tool => tool.category === categories[selectedTab]);

  const handleToolClick = (item) => {
    setSelectedTool(item.path);
    navigate(item.path);
  };

  const sidebarContent = () => (
    <Paper
      elevation={0}
      sx={{
        width: 280,
        bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'white',
        borderRight: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar sx={{ width: 48, height: 48, bgcolor: TOOL_COLORS.PDF.primary }}>
            <FaFilePdf size={28} color="white" />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>PDF Tools</Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {permittedInterfaces.length} tools available
            </Typography>
          </Box>
        </Box>
      </Box>
      <Tabs
        orientation="vertical"
        value={selectedTab}
        onChange={(e, newValue) => { setSelectedTab(newValue); if (isMobile) setSidebarOpen(false); }}
        sx={{
          borderRight: 0,
          '& .MuiTabs-indicator': { left: 0, width: 4, bgcolor: TOOL_COLORS.PDF.primary },
        }}
      >
        {categories.map((category, index) => (
          <Tab
            key={category}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
                <Typography variant="body2">{category}</Typography>
                <Chip
                  label={index === 0 ? permittedInterfaces.length : permittedInterfaces.filter(t => t.category === category).length}
                  size="small"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              </Box>
            }
            sx={{
              textAlign: 'left', alignItems: 'flex-start', px: 3, py: 1.5, minHeight: 48, textTransform: 'none',
              position: 'relative', overflow: 'hidden', transition: 'all 0.2s ease',
              '&.Mui-selected': { bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50' },
              '&:hover': { bgcolor: TOOL_COLORS.PDF.light, transform: 'translateX(4px)' },
              '&::after': {
                content: '""', position: 'absolute', bottom: 0, left: '50%', width: '0%', height: '3px',
                borderRadius: '2px', transform: 'translateX(-50%)', transition: 'width 0.3s ease-in-out',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 10' preserveAspectRatio='none'%3E%3Cpath d='M0 5 Q25 0 50 5 T100 5' fill='none' stroke='%23DC143C' stroke-width='4'/%3E%3C/svg%3E")`,
                backgroundSize: '100% 100%',
              },
              '&:hover::after': { width: '80%' },
            }}
          />
        ))}
      </Tabs>
      <Box sx={{ mt: 'auto', p: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>Quick Stats</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">Recently Used</Typography>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>{permittedInterfaces.filter(t => lastUsedMap[t.path]).length}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">Favorites</Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#FFD700' }}>{favorites.length}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">New Tools</Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#FF6B6B' }}>{permittedInterfaces.filter(t => t.isNew).length}</Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: theme.palette.background.default }}>
      {isMobile ? (
        <Drawer anchor="left" open={sidebarOpen} onClose={() => setSidebarOpen(false)} PaperProps={{ sx: { width: 280 } }}>
          {sidebarContent()}
        </Drawer>
      ) : (
        sidebarContent()
      )}

      {/* Main Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'white',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isMobile && (
              <IconButton onClick={() => setSidebarOpen(true)} sx={{ mr: 1 }}>
                <MenuIcon />
              </IconButton>
            )}
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                {categories[selectedTab]} Tools
              </Typography>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                Select a tool to begin working with your PDF documents
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Tools Grid */}
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(350px, 1fr))' }, gap: 2 }}>
            {filteredTools.map((item) => {
              const IconComponent = getToolIcon(item.name);
              const isFav = isFavorite(item.path);
              const isSelected = selectedTool === item.path;
              
              return (
                <Paper
                  key={item.path}
                  elevation={0}
                  sx={{
                    border: `2px solid ${isSelected ? TOOL_COLORS.PDF.primary : theme.palette.divider}`,
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    bgcolor: isSelected
                      ? TOOL_COLORS.PDF.light
                      : 'transparent',
                    '&:hover': {
                      borderColor: TOOL_COLORS.PDF.primary,
                      transform: 'translateX(4px)',
                      boxShadow: theme.shadows[2],
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      width: '0%',
                      height: '4px',
                      background: TOOL_COLORS.PDF.primary,
                      borderRadius: '2px',
                      transform: 'translateX(-50%)',
                      transition: 'width 0.4s ease-in-out',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 10' preserveAspectRatio='none'%3E%3Cpath d='M0 5 Q25 0 50 5 T100 5' fill='none' stroke='%23DC143C' stroke-width='4'/%3E%3C/svg%3E")`,
                      backgroundSize: '100% 100%',
                    },
                    '&:hover::after': {
                      width: '90%',
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => handleToolClick(item)}
                    sx={{ p: 2.5, height: '100%' }}
                  >
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* Icon Section */}
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1,
                        bgcolor: TOOL_COLORS.PDF.light,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <IconComponent size={24} color={TOOL_COLORS.PDF.primary} />
                    </Box>

                    {/* Content Section */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" sx={{ 
                            fontWeight: 600,
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {item.name}
                          </Typography>
                          {item.isNew && (
                            <Chip
                              label="NEW"
                              size="small"
                              sx={{
                                bgcolor: 'error.main',
                                color: 'white',
                                height: 20,
                                fontSize: '0.6rem',
                                fontWeight: 700,
                                ml: 1,
                                flexShrink: 0
                              }}
                            />
                          )}
                        </Box>
                        <Tooltip title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite({ text: item.name, path: item.path, category: 'pdf' });
                            }}
                            sx={{ 
                              ml: 1,
                              color: isFav ? '#FFD700' : theme.palette.action.disabled,
                            }}
                          >
                            {isFav ? '★' : '☆'}
                          </IconButton>
                        </Tooltip>
                      </Box>
                      
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          mb: 1,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {item.description}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FaClock size={12} color={theme.palette.text.secondary} />
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                          {getLastUsed(item.path)}
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        <Chip
                          label={item.category}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            bgcolor: TOOL_COLORS.PDF.light,
                            color: TOOL_COLORS.PDF.primary,
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                  </CardActionArea>
                </Paper>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PDFLanding;